/**
 * 앱 등록 팝업 (2026-09-14) — `scripts/ops/publish-app.sh` 의 네 단계를 화면으로 옮긴 것.
 *
 *   ① 검사   app-meta.json 을 읽고 고른 APK 와 크기 · sha256 을 맞춘다 (올리기 «전»에 — 200MB 를 올린 뒤 듣지 않으려고)
 *   ② 등록   POST /system/app-releases → PENDING 행 + presigned PUT (서버가 패키지 · 번호 증가 · 지문을 본다)
 *   ③ 업로드 presigned PUT 으로 MinIO 에 직접 (헤더 없음 · 진행률)
 *   ④ 확정   finalize → 서버가 크기 · zip · sha256 재계산 → 초안(DRAFT) 또는 바로 게시(PUBLISHED)
 *
 * ★ 실패하면 어디서 멈췄는지 보여 주고 **같은 팝업에서 다시** 할 수 있다 — 서버는 같은 번호의 PENDING 을 덮어쓴다(계약 §1).
 * ★ iOS 는 서버가 아직 받지 않는다(`PLATFORM_NOT_SUPPORTED`) — 고르지 못하게 하고 이유를 적는다.
 */
import { useEffect, useRef, useState } from 'react';
import { X, Loader2, CheckCircle, Upload } from '../../../icons';
import {
  createAppRelease, finalizeAppRelease, uploadToPresignedUrl, type AppReleaseMeta, type AppReleaseTransitionResult,
} from '../../../api/appReleases';
import { parseAppMeta, checkFileAgainstMeta, sha256OfBlob, formatBytes } from './appReleaseIntake';

type Step = 'idle' | 'creating' | 'uploading' | 'finalizing' | 'done';

const STEPS: Array<{ key: Step; label: string }> = [
  { key: 'creating', label: '② 등록' },
  { key: 'uploading', label: '③ 업로드' },
  { key: 'finalizing', label: '④ 확정' },
];

export function errText(e: any, fallback: string): string {
  const status = e?.response?.status;
  const data = e?.response?.data;
  // ★ 서버가 계약 코드(`code`)를 준 404/403 은 그 말이 답이다(TENANT_NOT_FOUND · APP_RELEASE_NOT_FOUND …).
  //   「재배포」 안내는 **맨몸 404**(라우트 자체가 없어 BFF/Fastify 가 준 것)에만 — 리뷰-화면 #1332.
  if (status === 404 && !data?.code) return '서버가 아직 이 기능을 모릅니다 — 관리 서버(chat-server)를 다시 배포해야 합니다.';
  if (status === 403 && !data?.message) return '시스템 관리자(SYSTEM_ADMIN)만 앱을 등록할 수 있습니다.';
  // 504 = BFF 가 30초를 못 기다린 것 — 서버는 확정을 끝냈을 수 있다(리뷰 #1332). 다시 누르게 하지 말고 목록을 보게 한다.
  if (status === 504) return e?.response?.data?.message ?? '응답이 늦습니다 — 서버가 아직 처리 중일 수 있습니다. 목록을 새로고침해 상태를 확인하세요.';
  return e?.response?.data?.message ?? e?.response?.data?.error ?? e?.message ?? fallback;
}

export function AppReleaseRegisterModal({ onClose, onDone }: {
  onClose: () => void;
  /** 확정까지 끝났을 때 — 화면이 목록을 되읽고 안내를 띄운다. */
  onDone: (result: AppReleaseTransitionResult) => void;
}) {
  const [metaName, setMetaName] = useState<string | null>(null);
  const [meta, setMeta] = useState<AppReleaseMeta | null>(null);
  const [metaProblems, setMetaProblems] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [fileSha, setFileSha] = useState<string | null>(null);
  const [hashing, setHashing] = useState(false);
  const [notes, setNotes] = useState('');
  const [buildNumber, setBuildNumber] = useState('');
  const [minSupported, setMinSupported] = useState('');
  const [publish, setPublish] = useState(false);
  const [step, setStep] = useState<Step>('idle');
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const metaInput = useRef<HTMLInputElement>(null);
  const apkInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && step === 'idle') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose, step]);

  async function takeMeta(f: File | undefined) {
    if (!f) return;
    setMetaName(f.name);
    const text = await f.text();
    const r = parseAppMeta(text);
    setMeta(r.meta); setMetaProblems(r.problems);
  }

  async function takeApk(f: File | undefined) {
    if (!f) return;
    setFile(f); setFileSha(null); setHashing(true);
    try { setFileSha(await sha256OfBlob(f)); }
    catch { setFileSha(null); }
    finally { setHashing(false); }
  }

  const fileProblems = meta && file ? checkFileAgainstMeta(meta, file, fileSha) : [];
  const busy = step !== 'idle' && step !== 'done';
  // 최소 지원 versionCode 가 이 버전보다 크면 서버가 400 을 준다 — 안내만 하고 단추를 살려 두면 헛걸음(리뷰-화면 #1332).
  const minTooHigh = !!meta && minSupported.trim() !== '' && Number(minSupported) > meta.versionCode;
  const canSubmit = !!meta && !!file && metaProblems.length === 0 && fileProblems.length === 0 && !hashing && !busy && !minTooHigh;

  async function run() {
    if (!meta || !file) return;
    setErr(null);
    try {
      setStep('creating');
      const min = minSupported.trim() === '' ? null : Number(minSupported);
      const created = await createAppRelease({
        platform: 'android',
        meta,
        releaseNotes: notes,
        minSupportedVersionCode: min !== null && Number.isInteger(min) && min > 0 ? min : null,
        buildNumber: buildNumber.trim() || null,
      });
      setStep('uploading'); setProgress(0);
      await uploadToPresignedUrl(created.upload.uploadUrl, file, (loaded, total) => {
        setProgress(total > 0 ? Math.round((loaded / total) * 100) : 0);
      });
      setStep('finalizing');
      const done = await finalizeAppRelease(created.release.id, publish);
      setStep('done');
      onDone(done);
    } catch (e: any) {
      setErr(errText(e, '등록 실패'));
      setStep('idle');
    }
  }

  const stepIdx = STEPS.findIndex((s) => s.key === step);

  return (
    <div className="svc-modal__backdrop" role="presentation" onClick={busy ? undefined : onClose}>
      <div className="svc-modal ar-modal" role="dialog" aria-modal="true" aria-labelledby="ar-reg-title" onClick={(e) => e.stopPropagation()}>
        <div className="svc-modal__head">
          <h3 id="ar-reg-title">앱 등록 (Android)</h3>
          <button type="button" className="svc-modal__close" onClick={onClose} disabled={busy} aria-label="닫기"><X size={16} /></button>
        </div>
        <div className="svc-modal__body">
          {err && (
            <div className="ar-note ar-note--bad" role="alert">
              <button type="button" className="ar-note__x" aria-label="닫기" onClick={() => setErr(null)}>×</button>
              {err}
            </div>
          )}

          <div className="svc-field">
            <label>app-meta.json *</label>
            <div className="ar-file">
              <input ref={metaInput} type="file" accept=".json,application/json" hidden onChange={(e) => void takeMeta(e.target.files?.[0])} />
              <button type="button" className="um-btn um-btn--sm" disabled={busy} onClick={() => metaInput.current?.click()}>
                <Upload size={13} /> meta 고르기
              </button>
              <span className="ar-file__name">{metaName ?? '(빌드가 만든 app-meta.json — gradle writeAppMetaRelease)'}</span>
            </div>
            {metaProblems.length > 0 && (
              <ul className="ar-problems" role="alert">{metaProblems.map((p) => <li key={p}>{p}</li>)}</ul>
            )}
            {meta && (
              <dl className="ar-kv">
                <dt>package</dt><dd className="ar-mono">{meta.package}</dd>
                <dt>버전</dt><dd>{meta.versionName} <span className="ar-muted">(versionCode {meta.versionCode})</span></dd>
                <dt>크기</dt><dd>{formatBytes(meta.size)} <span className="ar-muted">({meta.size.toLocaleString()} 바이트)</span></dd>
                <dt>sha256</dt><dd className="ar-mono">{meta.sha256}</dd>
                <dt>서명 지문</dt><dd className="ar-mono">{meta.certSha256}</dd>
              </dl>
            )}
          </div>

          <div className="svc-field">
            <label>설치 파일 (.apk) *</label>
            <div className="ar-file">
              <input ref={apkInput} type="file" accept=".apk,application/vnd.android.package-archive" hidden onChange={(e) => void takeApk(e.target.files?.[0])} />
              <button type="button" className="um-btn um-btn--sm" disabled={busy} onClick={() => apkInput.current?.click()}>
                <Upload size={13} /> APK 고르기
              </button>
              <span className="ar-file__name">{file ? `${file.name} · ${formatBytes(file.size)}` : '(최대 300MB)'}</span>
              {hashing && <span className="ar-file__hint"><Loader2 size={12} className="ar-spin" /> sha256 재는 중…</span>}
              {!hashing && file && meta && fileProblems.length === 0 && (
                <span className="ar-ok"><CheckCircle size={13} /> meta 와 일치{fileSha ? '' : ' (크기만 확인 — sha256 은 서버가 잰다)'}</span>
              )}
            </div>
            {fileProblems.length > 0 && (
              <ul className="ar-problems" role="alert">{fileProblems.map((p) => <li key={p}>{p}</li>)}</ul>
            )}
          </div>

          <div className="svc-field">
            <label>변경 내용</label>
            <textarea rows={3} value={notes} disabled={busy} onChange={(e) => setNotes(e.target.value)} placeholder="사원에게 보이는 변경 내용 (선택)" />
          </div>

          <div className="svc-row2">
            <div className="svc-field">
              <label>빌드 번호</label>
              <input value={buildNumber} disabled={busy} placeholder="예: ci-3481 (선택)" onChange={(e) => setBuildNumber(e.target.value)} />
            </div>
            <div className="svc-field">
              <label>최소 지원 versionCode</label>
              <input type="number" min={1} value={minSupported} disabled={busy} placeholder="이보다 낮은 앱은 강제 업데이트 (선택)" onChange={(e) => setMinSupported(e.target.value)} />
              {minTooHigh && (
                <span className="svc-field__hint">이 버전(versionCode {meta.versionCode})보다 클 수 없습니다.</span>
              )}
            </div>
          </div>

          <label className="ar-check">
            <input type="checkbox" checked={publish} disabled={busy} onChange={(e) => setPublish(e.target.checked)} />
            확정한 뒤 바로 게시한다 <span className="ar-muted">(끄면 초안으로 두고 나중에 게시 · 게시하면 이전 게시본은 최신 3개만 남는다)</span>
          </label>

          <div className="svc-field">
            <label>진행</label>
            <div className="ar-steps" aria-live="polite">
              <span className={`ar-step ${meta && file && fileProblems.length === 0 && !hashing ? 'is-done' : ''}`}>① 검사</span>
              {STEPS.map((s, i) => (
                <span key={s.key} className={`ar-step ${step === s.key ? 'is-on' : (stepIdx > i || step === 'done') ? 'is-done' : ''}`}>
                  {step === s.key && <Loader2 size={11} className="ar-spin" />}
                  {s.label}{s.key === 'uploading' && step === 'uploading' ? ` ${progress}%` : ''}
                </span>
              ))}
            </div>
            {step === 'uploading' && (
              <div className="ar-progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
                <div className="ar-progress__bar" style={{ width: `${progress}%` }} />
              </div>
            )}
            <span className="svc-field__hint">
              iOS 는 아직 등록할 수 없습니다(서버가 받지 않음 — 인증서 결정 뒤 별도 계약). 첫 등록의 서명 지문이 고정되고, 이후 다른 지문은 거절됩니다.
            </span>
          </div>
        </div>
        <div className="svc-modal__foot">
          <span className="spacer" />
          <button type="button" className="svc-mbtn" onClick={onClose} disabled={busy}>취소</button>
          <button type="button" className="svc-mbtn svc-mbtn--primary" onClick={() => void run()} disabled={!canSubmit}>
            {busy ? '진행 중…' : publish ? '등록하고 게시' : '등록(초안)'}
          </button>
        </div>
      </div>
    </div>
  );
}
